/**
 * processedCapture — port of casting's utils/processedCapture.js.
 *
 * Drives ONE camera capture through a <canvas> so a single source feeds BOTH
 * the embedded Jitsi call and the local MediaRecorder with identical processing
 * (digital zoom / center-crop). Without this, the recorder and the Jitsi iframe
 * each open their own getUserMedia — two captures of the same camera, with no
 * shared processing. Here the wrapper owns the capture: source stream → canvas
 * (zoom) → canvas.captureStream() → fed to the recorder, and the canvas video
 * track is what we hand Jitsi via api.setLocalVideoTrack().
 *
 * The output is always the canvas size (the source resolution), so zoom is a
 * center-crop scaled to fill — the recorded file and the live call stay identical.
 */
export interface ProcessedCapture {
  stream: MediaStream;                 // canvas video + source audio — give this to MediaRecorder
  readonly videoTrack: MediaStreamTrack; // the canvas video track — clone() it for api.setLocalVideoTrack
  setZoom: (z: number) => void;        // 1 = no zoom; >1 = center zoom
  getZoom: () => number;
  ready: Promise<void>;                // resolves once the source video is playing
  setSource: (s: MediaStream) => void; // swap cameras without rebuilding the pipeline
  stop: () => void;                    // tear down the canvas (does NOT stop the source camera)
}

export function createProcessedCapture(sourceStream: MediaStream, { fps = 30 }: { fps?: number } = {}): ProcessedCapture {
  const srcVideoTrack = sourceStream.getVideoTracks()[0];
  if (!srcVideoTrack) throw new Error('createProcessedCapture: source stream has no video track');

  const settings = srcVideoTrack.getSettings ? srcVideoTrack.getSettings() : ({} as MediaTrackSettings);
  const width = settings.width || 1920;
  const height = settings.height || 1080;

  // Hidden <video> playing the raw camera, sampled into the canvas each frame.
  // Attached to the page (rendered but invisible) so the browser actually
  // DECODES frames — a detached <video> isn't reliably decoded for some virtual
  // cameras (OBS, Snap): the raw track works in WebRTC but the canvas stays black.
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;pointer-events:none;z-index:-1;';
  document.body.appendChild(video);
  video.srcObject = new MediaStream([srcVideoTrack]);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;

  let zoom = 1;
  let rafId: number | null = null;
  let stopped = false;

  const draw = () => {
    if (stopped) return;
    const vw = video.videoWidth || width;
    const vh = video.videoHeight || height;
    if (vw && vh) {
      // Wrapped so a transient drawImage exception can't kill the rAF loop.
      try {
        const z = zoom > 1 ? zoom : 1;
        const sw = vw / z;
        const sh = vh / z;
        ctx.drawImage(video, (vw - sw) / 2, (vh - sh) / 2, sw, sh, 0, 0, canvas.width, canvas.height);
      } catch { /* survive */ }
    }
    rafId = requestAnimationFrame(draw);
  };

  // Start drawing whether or not play() resolves (autoplay policies vary); a
  // muted, srcObject-backed video generally plays without a gesture.
  const ready = Promise.resolve(video.play()).catch(() => {}).then(() => { draw(); });

  const canvasStream = canvas.captureStream(fps);
  const stream = new MediaStream([...canvasStream.getVideoTracks(), ...sourceStream.getAudioTracks()]);

  return {
    stream,
    get videoTrack() { return canvasStream.getVideoTracks()[0]; },
    setZoom(z: number) { const n = Number(z); zoom = isFinite(n) && n > 1 ? n : 1; },
    getZoom() { return zoom; },
    ready,
    setSource(newStream: MediaStream) {
      const v = newStream.getVideoTracks()[0];
      if (v) {
        video.srcObject = new MediaStream([v]);
        Promise.resolve(video.play()).catch(() => {});
      }
      stream.getAudioTracks().forEach(t => stream.removeTrack(t));
      newStream.getAudioTracks().forEach(t => stream.addTrack(t));
    },
    stop() {
      stopped = true;
      if (rafId) cancelAnimationFrame(rafId);
      // Stop only the canvas track — never the source camera the page owns.
      canvasStream.getTracks().forEach(t => t.stop());
      try { video.pause(); video.srcObject = null; video.remove(); } catch { /* teardown races */ }
    },
  };
}
